/**
 * Dashboard Page
 *
 * Main dashboard with sidebar navigation, header, welcome section,
 * urgent deadlines alert, advocacy suite, AI insights, activity stats,
 * widgets, and activity timeline.
 *
 * Based on DASHBOARD_FUNCTIONAL_SPEC.md - Phase 1, 2 & 3 implementation.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useCaseFile } from '../contexts/CaseFileContext';
import {
  Sidebar,
  Header,
  WelcomeSection,
  AdvocacySuite,
  ActivityStats,
  WatchtowerAlertsWidget,
  RecentCareLogsWidget,
  RecentActivityTimeline,
  // AIInsightsSection, // TODO: Re-enable when backend getAIInsights is implemented
} from '../components/dashboard';
import { UrgentDeadlinesAlert } from '../components/deadlines';
import { LockedCaseFileBanner } from '../components/authorization';

interface LocationState {
  message?: string;
}

interface BadgeCounts {
  watchtower?: number;
  insights?: number;
  notifications?: number;
}

export function Dashboard(): JSX.Element {
  const location = useLocation();
  const locationState = location.state as LocationState | null;

  // Use CaseFile context for global state
  const {
    caseFiles,
    activeCaseFileId,
    activeCaseFile,
    setActiveCaseFileId,
    refreshCaseFiles,
  } = useCaseFile();

  const [successMessage, setSuccessMessage] = useState<string | null>(
    locationState?.message || null
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [badgeCounts, setBadgeCounts] = useState<BadgeCounts>({});

  // Clear success message from location state
  useEffect(() => {
    if (locationState?.message) {
      window.history.replaceState({}, document.title);
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [locationState]);

  // Callback from ActivityStats to update sidebar badge counts (avoids duplicate API call)
  const handleStatsLoaded = useCallback((stats: { upcomingDeadlines: number }) => {
    setBadgeCounts({
      watchtower: stats.upcomingDeadlines,
      insights: 0,
      notifications: 0,
    });
  }, []);

  // Get active care recipient info
  const careRecipientName = activeCaseFile?.name;
  const activeCaseFilePermission = activeCaseFile?.userPermissions;

  const handleQuickLogClick = () => {
    // TODO: Open quick log modal or navigate to care log
    console.log('Quick Log Entry clicked');
  };

  const handleCaseFileChange = (caseFileId: string) => {
    setActiveCaseFileId(caseFileId);
  };

  const handleJoinedCareTeam = (caseFileId: string, caseFileName: string) => {
    // Show success message
    setSuccessMessage(`Successfully joined ${caseFileName}!`);
    // Set the joined CaseFile as active
    setActiveCaseFileId(caseFileId);
    // Refresh the CaseFiles list
    refreshCaseFiles();
  };

  return (
    <div className="min-h-screen bg-airy flex">
      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <Sidebar className="h-screen sticky top-0" activeCaseFileId={activeCaseFileId} badgeCounts={badgeCounts} />
      </div>

      {/* Sidebar - Mobile Overlay */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-ink/40 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
            <Sidebar className="h-full" activeCaseFileId={activeCaseFileId} badgeCounts={badgeCounts} />
          </div>
        </>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <Header
          caseFiles={caseFiles.map((cf) => ({ id: cf.caseFileId, name: cf.name }))}
          activeCaseFileId={activeCaseFileId}
          onCaseFileChange={handleCaseFileChange}
          onMenuClick={() => setSidebarOpen(true)}
          showMobileMenu={true}
          onJoinedCareTeam={handleJoinedCareTeam}
        />

        {/* Main Content */}
        <main id="main-content" tabIndex={-1} className="flex-1 p-4 lg:p-8 space-y-6 lg:space-y-8 overflow-y-auto outline-none">
          {/* Success message */}
          {successMessage && (
            <div className="alert alert-success flex justify-between items-center">
              <span>{successMessage}</span>
              <button
                onClick={() => setSuccessMessage(null)}
                className="text-deep-forest hover:text-ink text-xl leading-none"
              >
                &times;
              </button>
            </div>
          )}

          {/* Locked CaseFile Banner */}
          {activeCaseFile?.isLocked && (
            <LockedCaseFileBanner
              caseFileId={activeCaseFile.caseFileId}
              caseFileName={activeCaseFile.name}
              lockReason={activeCaseFile.lockReason}
              careRecipientName={activeCaseFile.name}
              careRecipientDob={activeCaseFile.dateOfBirth}
              careRecipientEmail={activeCaseFile.careRecipientEmail}
              onUnlocked={() => refreshCaseFiles()}
            />
          )}

          {/* Welcome Section */}
          <WelcomeSection
            careRecipientName={careRecipientName}
            userPermission={activeCaseFilePermission}
            onQuickLogClick={handleQuickLogClick}
          />

          {/* Urgent Deadlines Alert - Shows if any deadlines within 14 days */}
          <UrgentDeadlinesAlert
            activeCaseFileId={activeCaseFileId}
            daysAhead={14}
          />

          {/* Advocacy Suite - 4 Module Cards */}
          <AdvocacySuite activeCaseFileId={activeCaseFileId} />

          {/* AI Insights Section - TODO: Re-enable when backend getAIInsights is implemented
          <AIInsightsSection activeCaseFileId={activeCaseFileId} />
          */}

          {/* Activity Stats - 4 Metric Cards */}
          <ActivityStats activeCaseFileId={activeCaseFileId} onStatsLoaded={handleStatsLoaded} />

          {/* Two-Column Widget Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Watchtower Alerts Widget */}
            <WatchtowerAlertsWidget activeCaseFileId={activeCaseFileId} />

            {/* Recent Care Logs Widget */}
            <RecentCareLogsWidget activeCaseFileId={activeCaseFileId} />
          </div>

          {/* Recent Activity Timeline */}
          <RecentActivityTimeline activeCaseFileId={activeCaseFileId} />
        </main>
      </div>
    </div>
  );
}

export default Dashboard;
